import React, { useCallback, useState } from "react";
import { useOkapiKy } from "@folio/stripes/core";
import { Button, Col, ErrorModal, KeyValue, MessageBanner, Modal, Row, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchObalkyKnihSyncStatus, startObalkyKnihSync, } from "../../api/adminService";
var STATUS_QUERY_KEY = ["agency", "admin", "obalkyKnihSyncStatus"];
var POLL_INTERVAL_MS = 2000;
var RESULT_FIELDS = [
    "checked",
    "found",
    "updated",
    "failedChunks",
    "removed",
];
/**
 * Administration section that starts the obalkyknih.cz cover sync for all titles (the backend runs
 * it with force=true, in the background) and follows its progress until it finishes.
 */
export var ObalkyKnihSync = function () {
    var _a;
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _b = useIntl(), formatMessage = _b.formatMessage, formatDate = _b.formatDate, formatNumber = _b.formatNumber;
    var _c = useState(false), resultOpen = _c[0], setResultOpen = _c[1];
    var _d = useState(null), errorMessage = _d[0], setErrorMessage = _d[1];
    var showError = useCallback(function (error) {
        return setErrorMessage(formatMessage({ id: "ui-br-agency.admin.obalky-knih.error" }, { reason: error.message }));
    }, [formatMessage]);
    var status = useQuery(STATUS_QUERY_KEY, function () { return fetchObalkyKnihSyncStatus(ky); }, {
        refetchInterval: function (current) {
            return (current === null || current === void 0 ? void 0 : current.state) === "RUNNING" ? POLL_INTERVAL_MS : false;
        },
        onError: showError,
    }).data;
    var _e = useMutation(function () { return startObalkyKnihSync(ky); }, {
        onSuccess: function (started) {
            queryClient.setQueryData(STATUS_QUERY_KEY, started);
            setResultOpen(true);
        },
        onError: showError,
    }), start = _e.mutate, isStarting = _e.isLoading;
    var isRunning = (status === null || status === void 0 ? void 0 : status.state) === "RUNNING";
    var isDone = (status === null || status === void 0 ? void 0 : status.state) === "FINISHED" || (status === null || status === void 0 ? void 0 : status.state) === "FAILED";
    var handleStart = useCallback(function () { return start(); }, [start]);
    var openResult = useCallback(function () { return setResultOpen(true); }, []);
    var closeResult = useCallback(function () { return setResultOpen(false); }, []);
    var closeError = useCallback(function () { return setErrorMessage(null); }, []);
    var formatTimestamp = function (value) {
        return value ? formatDate(value, { dateStyle: "short", timeStyle: "medium" }) : "";
    };
    var stateMessage = status && status.state !== "IDLE" && (React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.state.".concat(status.state), values: {
            processed: formatNumber(status.processedTitles),
            total: formatNumber(status.totalTitles),
            finishedAt: formatTimestamp(status.finishedAt),
            error: (_a = status.error) !== null && _a !== void 0 ? _a : "",
        } }));
    return (React.createElement(React.Fragment, null,
        React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.title", tagName: "h1" }),
        React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.description", tagName: "p" }),
        status && !status.scheduledSyncEnabled && (React.createElement(MessageBanner, { type: "warning" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.scheduledDisabled" }))),
        stateMessage && React.createElement("p", null, stateMessage),
        React.createElement(Modal, { label: React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.title" }), open: resultOpen, onClose: closeResult, dismissible: true, size: "medium" },
            stateMessage,
            (status === null || status === void 0 ? void 0 : status.state) === "FINISHED" && status.result && (React.createElement(Row, null, RESULT_FIELDS.map(function (field) {
                var _a, _b;
                return (React.createElement(Col, { xs: true, key: field },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.result.".concat(field) }), value: formatNumber((_b = (_a = status.result) === null || _a === void 0 ? void 0 : _a[field]) !== null && _b !== void 0 ? _b : 0) })));
            })))),
        React.createElement(ErrorModal, { label: React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.title" }), open: errorMessage !== null, content: errorMessage !== null && errorMessage !== void 0 ? errorMessage : "", onClose: closeError }),
        React.createElement(Row, null,
            React.createElement(Col, null,
                React.createElement(Button, { buttonStyle: "primary", onClick: handleStart, disabled: isStarting || isRunning }, isStarting || isRunning ? (React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.button.running" })) : (React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.button" }))),
                isDone && (React.createElement(Button, { onClick: openResult },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.admin.obalky-knih.button.result" })))))));
};
